// @ts-nocheck
/**
 * Finance agent content for the shared Ophelos AI panel (`AIChatPanel` + `ChatHeader`).
 * Renders only the inner column (messages, lifecycle, action cards, input) — no duplicate chrome.
 */

import { Alert, AlertAction, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { cn } from '@ophelos/ui/lib/utils';
import { Sparkles } from 'lucide-react';
import type React from 'react';
import { lazy, Suspense, useCallback, useState } from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  useBankStatements,
  useManualMatchBankStatement,
  usePspReceiptLines,
  useReclassifyBankStatement,
} from '@/apps/finances/hooks/useBankStatements';
import { financeHref, useFinanceBook } from '@/apps/finances/shell/book';
import { isFinanceLogicalPath } from '@/apps/finances/shell/paths';
import { extractFinanceClassifyBlock } from '@/apps/finances/utils/financeClassifyBlock';
import { extractFinanceMatchBlock } from '@/apps/finances/utils/financeMatchBlock';
import { resolveActionCard } from '@/apps/finances/utils/resolveActionCard';
import { useFinanceAgentChat } from '@/components/ai-chat/hooks/useFinanceAgentChat';
import { useHub } from '@/contexts/HubContext';
import { ChatInput } from '../../_shared/ChatInput';
import { type ChatMessage, ChatMessages } from '../../_shared/ChatMessages';
import { WelcomeScreen } from '../../_shared/WelcomeScreen';
import { getAgentConfig } from '../../agent-registry';
import { BankLineClassifyCard } from './_partials/BankLineClassifyCard';
import { BankLineMatchPickerCard } from './_partials/BankLineMatchPickerCard';
import { BankStatementLifecycleFlow } from './_partials/BankStatementLifecycleFlow';
import { LifecycleFlow } from './_partials/LifecycleFlow';
import { MatchSuggestionCard } from './_partials/MatchSuggestionCard';
import { getLifecycleById } from './_partials/reconciliationLifecycles';
import { StatusGroupChooserCard } from './_partials/StatusGroupChooserCard';
import { StatusGroupView } from './_partials/StatusGroupView';

const PspLinePickerCard = lazy(() =>
  import('@/components/ai-chat/agents/finance/_partials/PspLinePickerCard').then((m) => ({ default: m.PspLinePickerCard }))
);

const contentBoxClass = 'rounded-xl border border-border bg-muted/30 dark:bg-muted/20 my-3';

type MarkdownComponentProps =
  | { href?: string; children?: React.ReactNode }
  | { children?: React.ReactNode }
  | { className?: string; children?: React.ReactNode };

/** Internal finance links carry the current book; a bare link would land on All Clients. */
function FinanceMarkdownLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const book = useFinanceBook();
  if (!href?.startsWith('/')) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
        {children}
      </a>
    );
  }
  const to = isFinanceLogicalPath(href) ? financeHref(href, book) : href;
  return (
    <Link to={to} className="text-primary underline underline-offset-2 hover:text-primary/80">
      {children}
    </Link>
  );
}

const markdownComponents: Record<string, React.ComponentType<MarkdownComponentProps>> = {
  a: FinanceMarkdownLink,
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className={`${contentBoxClass} p-3 overflow-x-auto`}>
      <table className="w-full text-xs border-collapse table-fixed">
        <colgroup>
          <col style={{ width: 'clamp(8rem, 22%, 14rem)' }} />
          <col style={{ width: '5rem' }} />
          <col style={{ width: 'auto' }} />
        </colgroup>
        {children}
      </table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border-b border-border px-2 py-1 text-left font-medium text-muted-foreground break-words">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-b border-border/50 px-2 py-1 break-words align-top">{children}</td>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className={`${contentBoxClass} p-3 overflow-x-auto text-xs leading-relaxed`}>{children}</pre>
  ),
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    const isBlock = className != null;
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return <code className="rounded bg-muted/50 px-1 py-0.5 text-xs">{children}</code>;
  },
  p: ({ children }: { children?: React.ReactNode }) => <p className="my-3 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="my-3 list-disc pl-5 space-y-1">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="my-3 list-decimal pl-5 space-y-1">{children}</ol>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2 className="mt-6 text-sm font-semibold first:mt-0">{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3 className="mt-6 text-sm font-medium first:mt-0">{children}</h3>,
  hr: () => <hr className="my-6 border-border first:mt-0" />,
};

/** Strip finance XML blocks and render with custom markdown components */
function transformFinanceContent(content: string, role: string): string {
  if (role !== 'assistant') return content;
  let cleaned = extractFinanceMatchBlock(content).contentWithoutBlock;
  cleaned = extractFinanceClassifyBlock(cleaned).contentWithoutBlock;
  return cleaned.replace(/\\n/g, '\n');
}

function FinanceAssistantContent({ content }: { content: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </Markdown>
  );
}

export function FinanceChatPanel() {
  const {
    messages: financeMessages,
    isLoading,
    error,
    sendMessage,
    clearError,
    canChat,
    lifecycleIdForFlow,
    briefingItemsForFlow,
    selectedBriefingItemId,
    setSelectedBriefingItemId,
    statusCountsForFlow,
    chatInput,
    setChatInput,
    dismissedMatchCard,
    setDismissedMatchCard,
    dismissedLinePickerCard,
    setDismissedLinePickerCard,
    chatOrigin,
  } = useFinanceAgentChat();
  const { selectedOperator } = useHub();
  const [dismissedClassifyCard, setDismissedClassifyCard] = useState(false);
  const [dismissedPspPickerCard, setDismissedPspPickerCard] = useState(false);
  const [selectedStatusGroup, setSelectedStatusGroup] = useState<string | null>(null);
  const [dismissedMatchPickerCard, setDismissedMatchPickerCard] = useState(false);
  const [justClassifiedLineId, setJustClassifiedLineId] = useState<string | null>(null);
  const [dismissedFindMatchesPrompt, setDismissedFindMatchesPrompt] = useState(false);

  // Extract action cards from last assistant message
  const lastAssistantIndex = (() => {
    let i = financeMessages.length - 1;
    while (i >= 0 && financeMessages[i].role !== 'assistant') i--;
    return i;
  })();
  const lastAssistantContent = lastAssistantIndex >= 0 ? financeMessages[lastAssistantIndex].content.replace(/\\n/g, '\n') : '';
  const extracted = lastAssistantIndex >= 0 ? extractFinanceMatchBlock(lastAssistantContent) : null;
  const classifyExtracted = lastAssistantIndex >= 0 ? extractFinanceClassifyBlock(lastAssistantContent) : null;
  const matchSuggestion = extracted?.matchPayload ?? null;
  const classifySuggestion = classifyExtracted?.classifyPayload ?? null;
  const showMatchCard = matchSuggestion != null && !dismissedMatchCard;
  const showClassifyCard = classifySuggestion != null && !dismissedClassifyCard && !showMatchCard;

  const actionCardType = resolveActionCard(chatOrigin);
  const hasAssistantReply = financeMessages.some((m) => m.role === 'assistant');

  const showStatusChooser =
    actionCardType === 'bank_line_match_picker' &&
    hasAssistantReply &&
    selectedStatusGroup === null &&
    !dismissedLinePickerCard &&
    !isLoading &&
    !showMatchCard;

  const assistantCount = financeMessages.filter((m) => m.role === 'assistant').length;
  const showMatchPickerAfterClassified =
    actionCardType === 'bank_line_match_picker' &&
    selectedStatusGroup === 'classified' &&
    assistantCount >= 2 &&
    !dismissedMatchPickerCard &&
    !isLoading &&
    !showMatchCard;

  const showPspPickerCard =
    actionCardType === 'psp_line_picker' &&
    financeMessages.some((m) => m.role === 'assistant') &&
    !dismissedPspPickerCard &&
    !isLoading &&
    !showMatchCard;

  const { data: classifiedLinesData } = useBankStatements({
    filters: { status: 'classified', pageSize: 50 },
    operatorId: selectedOperator?.id,
    enabled: showMatchPickerAfterClassified,
  });
  const classifiedLines = classifiedLinesData?.lines ?? [];

  const { data: pspReceiptData } = usePspReceiptLines(
    50,
    actionCardType === 'psp_line_picker' && financeMessages.some((m) => m.role === 'assistant')
  );
  const pspLines = pspReceiptData?.pages.flatMap((page) => page.data) ?? [];
  const manualMatchMutation = useManualMatchBankStatement({
    onSuccess: () => {
      setDismissedMatchCard(true);
      setDismissedLinePickerCard(true);
    },
    onError: (err) => {
      toast.error('Failed to apply match', {
        description: err instanceof Error ? err.message : 'Unable to match the bank line.',
      });
    },
  });
  const reclassifyMutation = useReclassifyBankStatement({
    onSuccess: (_data, variables) => {
      setDismissedClassifyCard(true);
      if (variables.lineId) {
        setJustClassifiedLineId(variables.lineId);
        setDismissedFindMatchesPrompt(false);
      }
    },
    onError: (err) => {
      toast.error('Failed to apply classification', {
        description: err instanceof Error ? err.message : 'Unable to reclassify the bank line.',
      });
    },
  });

  const handleSend = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage]
  );

  const hasUserMessage = financeMessages.some((m) => m.role === 'user');
  const showFinanceWelcome = !hasUserMessage && !isLoading;

  // Map finance messages to shared ChatMessage format
  const messages: ChatMessage[] = financeMessages.map((msg, idx) => ({
    id: `finance-${idx}`,
    role: msg.role,
    content: msg.content,
  }));

  // Streaming state adapter — finance uses isLoading boolean, not SSE streaming
  const streaming = isLoading ? { active: true, content: '' } : null;

  const selectedItem = selectedBriefingItemId ? briefingItemsForFlow.find((i) => i.id === selectedBriefingItemId) : null;
  const lifecycle = lifecycleIdForFlow ? getLifecycleById(lifecycleIdForFlow) : null;
  const isBranching = lifecycle?.type === 'branching';
  const flowProceedLabel =
    lifecycleIdForFlow === 'payouts'
      ? 'Go to Payouts'
      : lifecycleIdForFlow === 'bank_statements'
        ? 'Go to Bank statements'
        : lifecycleIdForFlow === 'psp_matching'
          ? 'Go to PSP Matching'
          : 'Proceed';

  // Lifecycle flows rendered above messages
  const lifecycleHeader = lifecycleIdForFlow ? (
    <div>
      {lifecycleIdForFlow === 'bank_statements' ? (
        <BankStatementLifecycleFlow proceedLabel={flowProceedLabel} proceedHref={lifecycle?.link} />
      ) : isBranching ? (
        <StatusGroupView
          lifecycleId={lifecycleIdForFlow}
          statusCounts={statusCountsForFlow}
          proceedLabel={flowProceedLabel}
          proceedHref={lifecycle?.link}
        />
      ) : (
        <>
          {briefingItemsForFlow.length > 0 && (
            <div className={cn(contentBoxClass, 'p-3 mb-3')}>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Pending items</p>
              <ul className="space-y-1 max-h-32 overflow-y-auto slim-scrollbar">
                {briefingItemsForFlow.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedBriefingItemId(selectedBriefingItemId === item.id ? null : item.id)}
                      className={cn(
                        'w-full text-left rounded-md px-2 py-1.5 text-xs transition-colors',
                        selectedBriefingItemId === item.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50 text-foreground'
                      )}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {selectedItem ? (
            <LifecycleFlow
              lifecycleId={lifecycleIdForFlow}
              currentStepId={selectedItem.current_step}
              proceedLabel={flowProceedLabel}
              proceedHref={selectedItem.link}
            />
          ) : (
            <LifecycleFlow lifecycleId={lifecycleIdForFlow} />
          )}
        </>
      )}
    </div>
  ) : null;

  const financeAgentConfig = getAgentConfig('finance');

  // Finance welcome screen as empty state
  const financeEmptyState = showFinanceWelcome ? (
    <WelcomeScreen agentConfig={financeAgentConfig} onChoosePrompt={(p) => void sendMessage(p.text)} disabled={!canChat || isLoading} />
  ) : !canChat ? (
    <p className="text-sm text-muted-foreground p-2">Select an operator to chat.</p>
  ) : undefined;

  const hasActionCard =
    (showStatusChooser && !!lifecycleIdForFlow) ||
    showMatchPickerAfterClassified ||
    showPspPickerCard ||
    (showClassifyCard && !!classifySuggestion) ||
    (justClassifiedLineId && !dismissedFindMatchesPrompt && !showMatchCard && !isLoading) ||
    (showMatchCard && !!matchSuggestion);

  // Action cards rendered between messages and input
  const actionCards = (
    <>
      {showStatusChooser && lifecycleIdForFlow && (
        <div className="shrink-0 px-3">
          <StatusGroupChooserCard
            statusCounts={statusCountsForFlow}
            lifecycleId={lifecycleIdForFlow}
            onChooseGroup={(statusId, prompt) => {
              setSelectedStatusGroup(statusId);
              sendMessage(prompt);
            }}
            onDismiss={() => setDismissedLinePickerCard(true)}
          />
        </div>
      )}

      {showMatchPickerAfterClassified && (
        <div className="shrink-0 px-3">
          <BankLineMatchPickerCard
            lines={classifiedLines}
            onFindMatches={(lineId) => {
              setDismissedMatchPickerCard(true);
              sendMessage(`Find matches for bank line ${lineId}`);
            }}
            onDismiss={() => setDismissedMatchPickerCard(true)}
            isLoading={isLoading}
          />
        </div>
      )}

      {showPspPickerCard && (
        <div className="shrink-0 px-3">
          <Suspense fallback={<div className="rounded-xl border border-border p-2.5 text-xs text-muted-foreground">Loading…</div>}>
            <PspLinePickerCard
              lines={pspLines}
              onAskAboutLine={(lineId) => {
                setDismissedPspPickerCard(true);
                sendMessage(`What do I do with PSP line ${lineId}?`);
              }}
              onDismiss={() => setDismissedPspPickerCard(true)}
              isLoading={isLoading}
            />
          </Suspense>
        </div>
      )}

      {showClassifyCard && classifySuggestion && (
        <div className="shrink-0 px-3">
          <BankLineClassifyCard
            bankLineId={classifySuggestion.bank_line_id}
            suggestedType={classifySuggestion.suggested_type}
            confidence={classifySuggestion.confidence}
            reasoning={classifySuggestion.reasoning}
            confidenceLevel={classifySuggestion.confidence_level}
            matchedPattern={classifySuggestion.matched_pattern}
            onClassify={(lineId, classificationType) => {
              reclassifyMutation.mutate({ lineId, classificationType });
            }}
            onDismiss={() => setDismissedClassifyCard(true)}
            isClassifying={reclassifyMutation.isPending}
          />
        </div>
      )}

      {justClassifiedLineId && !dismissedFindMatchesPrompt && !showMatchCard && !isLoading && (
        <div className="shrink-0 px-3">
          <div className="rounded-xl border border-border bg-muted/30 dark:bg-muted/20 p-3 text-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Line classified</p>
            <p className="text-muted-foreground text-xs mb-2.5">Want to find a matching case for this line?</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="default"
                className="text-brand-600 border-brand-200 bg-brand-50 hover:bg-brand-100 dark:text-brand-400 dark:border-brand-800 dark:bg-brand-900/30 dark:hover:bg-brand-800/50"
                onClick={() => {
                  setDismissedFindMatchesPrompt(true);
                  sendMessage(`Find matches for bank line ${justClassifiedLineId}`);
                }}
              >
                <Sparkles className="size-3.5 mr-1.5" />
                Find matches
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDismissedFindMatchesPrompt(true)}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {showMatchCard && matchSuggestion && (
        <div className="shrink-0 px-3">
          <MatchSuggestionCard
            bankLineId={matchSuggestion.bank_line_id}
            candidates={matchSuggestion.candidates}
            onMatch={(caseId) => {
              manualMatchMutation.mutate({
                lineId: matchSuggestion.bank_line_id,
                caseId,
              });
            }}
            onDismiss={() => setDismissedMatchCard(true)}
            isMatching={manualMatchMutation.isPending}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="flex-1 overflow-hidden">
        <ChatMessages
          messages={messages}
          streaming={streaming}
          transformContent={transformFinanceContent}
          emptyState={financeEmptyState}
          headerContent={lifecycleHeader}
          renderAssistantContent={(content) => <FinanceAssistantContent content={content} />}
        />
      </div>
      {actionCards}

      <div className="shrink-0 stack gap-2">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <AlertAction>
              <Button variant="destructive" size="sm" onClick={clearError}>
                Dismiss
              </Button>
            </AlertAction>
          </Alert>
        )}
        {!hasActionCard && (
          <ChatInput
            onSend={handleSend}
            disabled={!canChat || isLoading}
            placeholder={canChat ? 'Ask about finances…' : 'Select an operator to chat'}
            value={chatInput}
            onChange={setChatInput}
          />
        )}
      </div>
    </>
  );
}
